import type { ReactNode } from 'react';
import { Document, Page, View, Text, Image, Link } from '@react-pdf/renderer';
import { shared, ODO_LOGO_URL } from './styles';
import { ORTHOPULSE_INSTRUCTIONS_URL, START_DAY_INSTRUCTIONS_URL, shortenUrl } from './helpLinks';
import { ElasticsPdfSection } from './ElasticsPdfSection';
import { renderTiptap } from '../rich-editor/tiptap-pdf';
import { tiptapDocFromString } from '../rich-editor/plainText';
import type { TiptapDoc } from '../rich-editor/types';
import { formatDays } from '../clinical/formatDays';
import {
    deriveAlignerFields,
    anyAlignerFieldEnabled,
    showAlignerWear
} from '../clinical/alignerFields';
import { hasLegacyElastics, schemesHaveElastics } from '../clinical/elastics/persistence';
import type { ElasticScheme, LegacyElastics } from '../clinical/elastics/model';
import type { SectionToggles } from '../clinical/types';

/** SDI and RDI form data differ only in the next-appointment field name, which
 *  each wrapper maps into `nextAppointmentValue`. */
export interface ClinicalDocData {
    date: string;
    patientName: string;
    notes: string;
    notesDoc?: TiptapDoc;
    numberOfAligners: string;
    nextAppointmentValue: string;
    alignerWear: string;
    dmScanFrequency: string;
    alignerChangeFrequency: string;
    elasticsSchemes: ElasticScheme[];
    legacyElastics?: LegacyElastics;
}

/** The only content that differs between the SDI and RDI documents; structure
 *  and styling are shared so the two read as one system. */
export interface ClinicalDocVariant {
    title: string;
    /** Unique key prefix for the rendered rich-text notes. */
    notesKey: string;
    /** Optional disclaimer paragraph(s) after the shared extraction warning. */
    extraDisclaimer?: ReactNode;
    /** Heading of the aligner wear schedule sub-block. */
    alignerScheduleTitle: string;
    /** Optional note rendered under the aligner schedule heading. */
    alignerScheduleNote?: ReactNode;
    /** Trailing text of the "additional instructions" help link. */
    helpLinkAdditionalText: string;
}

interface ClinicalInstructionsDocumentProps {
    data: ClinicalDocData;
    hasOrthopulse: boolean;
    toggles: SectionToggles;
    variant: ClinicalDocVariant;
}

function FieldValue({ value }: { value: string }) {
    return (
        <View style={shared.infoValue}>
            <Text style={shared.infoValueText}>{value || 'N/A'}</Text>
        </View>
    );
}

function ScheduleField({ label, value }: { label: string; value: string }) {
    return (
        <View style={shared.scheduleRow}>
            <Text style={shared.scheduleLabel}>{label}</Text>
            <View style={shared.scheduleValue}>
                <Text style={shared.scheduleValueText}>{value || 'N/A'}</Text>
            </View>
        </View>
    );
}

function resolveNotesDoc(data: ClinicalDocData): TiptapDoc {
    if (data.notesDoc) return data.notesDoc;
    return tiptapDocFromString(data.notes);
}

function ClinicalInstructionsDocument({
    data,
    hasOrthopulse,
    toggles,
    variant
}: ClinicalInstructionsDocumentProps) {
    const { elasticsEnabled } = toggles;
    const af = deriveAlignerFields(toggles.alignerFields, toggles.alignerScheduleEnabled);
    const notesDoc = resolveNotesDoc(data);
    // Only print the elastics section when it's enabled AND actually has content —
    // an enabled-but-empty section (no placed elastics, no legacy values) is omitted.
    const showElastics =
        elasticsEnabled &&
        (schemesHaveElastics(data.elasticsSchemes ?? []) || hasLegacyElastics(data.legacyElastics));

    return (
        <Document>
            <Page size="LETTER" style={shared.page}>
                <View style={shared.pageContent}>
                    {/* Header */}
                    <View style={shared.header}>
                        <Text style={shared.title}>{variant.title}</Text>
                        <Image src={ODO_LOGO_URL} style={shared.logo} />
                    </View>

                    {/* Date / Patient */}
                    <View style={shared.infoRow}>
                        <View style={shared.infoCell}>
                            <Text style={shared.infoLabel}>Date</Text>
                            <FieldValue value={data.date} />
                        </View>
                        <View style={shared.infoCell}>
                            <Text style={shared.infoLabel}>Patient</Text>
                            <FieldValue value={data.patientName} />
                        </View>
                    </View>

                    <View style={shared.divider} />

                    {/* Disclaimer — shared extraction warning, plus any variant-specific paragraphs */}
                    <View style={shared.section}>
                        <Text style={shared.warningText}>
                            If the manufacturer form shows &quot;extraction&quot; this means the
                            tooth is &quot;excluded&quot; in the aligner.{' '}
                            <Text style={shared.warningEmphasis}>DO NOT extract tooth.</Text>
                        </Text>
                    </View>
                    {variant.extraDisclaimer}

                    <View style={shared.divider} />

                    {/* Notes */}
                    <View style={shared.section}>
                        <Text style={shared.sectionTitle}>Notes / Instructions:</Text>
                        <View style={shared.notesBox}>
                            {renderTiptap(notesDoc, variant.notesKey)}
                        </View>
                    </View>

                    {/* Aligner Schedule — per-field toggles; whole block hidden when all fields disabled */}
                    {anyAlignerFieldEnabled(af) && (
                        <>
                            <View style={shared.divider} />

                            {/* Aligner wear schedule sub-block — shown only when numberOfAligners or nextAppointment is on */}
                            {(af.numberOfAligners || af.nextAppointment) && (
                                <>
                                    <Text style={shared.sectionTitle}>
                                        {variant.alignerScheduleTitle}
                                    </Text>
                                    {variant.alignerScheduleNote}
                                    {af.numberOfAligners && (
                                        <ScheduleField
                                            label="Provide this many aligners to patient:"
                                            value={data.numberOfAligners}
                                        />
                                    )}
                                    {af.nextAppointment && (
                                        <ScheduleField
                                            label="Next appointment at aligner:"
                                            value={data.nextAppointmentValue}
                                        />
                                    )}
                                </>
                            )}

                            {/* DM Frequency sub-block */}
                            {(af.dmScanFrequency ||
                                af.alignerChangeFrequency ||
                                af.alignerWear) && (
                                <>
                                    <View style={shared.divider} />
                                    <Text style={shared.sectionTitle}>
                                        Dental Monitoring/Aligner Change Frequency:
                                    </Text>
                                    {af.dmScanFrequency && (
                                        <ScheduleField
                                            label="Patient scans every:"
                                            value={formatDays(data.dmScanFrequency)}
                                        />
                                    )}
                                    {af.alignerChangeFrequency && (
                                        <ScheduleField
                                            label="Patient aligner change frequency:"
                                            value={formatDays(data.alignerChangeFrequency)}
                                        />
                                    )}
                                    {af.alignerWear && showAlignerWear(af) && (
                                        <ScheduleField
                                            label="Patient aligner wear schedule:"
                                            value={data.alignerWear}
                                        />
                                    )}
                                    <View style={shared.section}>
                                        <Text style={shared.warningText}>
                                            *Note: the scan/aligner change frequency may change
                                            during treatment - if it does, the patient will be
                                            notified through Dental Monitoring.
                                        </Text>
                                    </View>
                                </>
                            )}
                        </>
                    )}

                    {/* Help links */}
                    <View style={shared.divider} />
                    <View style={shared.helpLinksContainer}>
                        {hasOrthopulse && (
                            <Text style={shared.helpLinkText}>
                                For Orthopulse instructions, visit here:{' '}
                                <Link src={ORTHOPULSE_INSTRUCTIONS_URL} style={shared.helpLink}>
                                    {shortenUrl(ORTHOPULSE_INSTRUCTIONS_URL)}
                                </Link>
                            </Text>
                        )}
                        <Text style={shared.helpLinkText}>
                            {variant.helpLinkAdditionalText}{' '}
                            <Link src={START_DAY_INSTRUCTIONS_URL} style={shared.helpLink}>
                                {shortenUrl(START_DAY_INSTRUCTIONS_URL)}
                            </Link>
                        </Text>
                    </View>

                    {/* Elastics — omitted entirely when disabled or empty, including its leading divider */}
                    {showElastics && (
                        <>
                            {/* minPresenceAhead keeps the heading from sitting alone at the
                                bottom of a page when the first scheme wouldn't fit below it. */}
                            <View minPresenceAhead={200} style={shared.divider} />

                            <View minPresenceAhead={180}>
                                <View
                                    style={{ flexDirection: 'row', alignItems: 'baseline', gap: 4 }}
                                >
                                    <Text style={shared.sectionTitle}>Elastics</Text>
                                </View>
                            </View>

                            <ElasticsPdfSection
                                schemes={data.elasticsSchemes ?? []}
                                legacy={data.legacyElastics}
                            />
                        </>
                    )}
                </View>

                {/* Fixed to every page's bottom: content spans 2 pages once elastics are included. */}
                <View
                    fixed
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 24,
                        right: 24,
                        alignItems: 'flex-end'
                    }}
                >
                    <Text
                        style={shared.footerText}
                        fixed
                        render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
}

export { ClinicalInstructionsDocument };
